import { useLayoutEffect, useRef, type MouseEvent } from 'react'
import { Ban, Phone, PhoneCall } from 'lucide-react'
import { Button, type ButtonSize } from '../ui/Button'
import { IconButton } from '../ui/IconButton'
import type { ArmedAction } from '../../hooks/useArmedAction'

/**
 * #193 — les DEUX gestes d'une ligne « À rappeler », chacun confirmé EN PLACE
 * (`useArmedAction`) : le premier tap pose la question dans le bouton lui-même,
 * le second l'exécute. Partagés par le panneau du téléphone et le menu du
 * badge : même geste, même libellé, partout.
 *
 * Les libellés arrivent TRADUITS de l'appelant (namespaces différents).
 *
 * #392 — variante `compact` (lignes de 44 px de l'onglet « À rappeler » du
 * poste) : AU REPOS, chaque geste est un bouton ICÔNE `sm` du kit (32 px
 * dessinés, cible de 44 px, nom accessible ET infobulle portés par
 * `IconButton`) ; ARMÉ, il redevient un bouton À LIBELLÉ qui pose la question
 * (« Appeler ? », « Confirmer ? »). Le geste destructif ne part donc jamais
 * d'une icône seule : l'icône n'arme que la confirmation nommée — le cas que
 * `IconButton` admet (DESIGN-SYSTEM §5). Le focus clavier suit la bascule
 * icône ↔ libellé (deux éléments distincts) : Entrée, Entrée confirme.
 */

export const recallKey = (number: string) => `recall:${number}`
export const dismissKey = (number: string) => `dismiss:${number}`

interface ActionButtonProps {
  number: string
  armed: ArmedAction
  /** Libellé au repos. */
  label: string
  /** Libellé armé — la QUESTION (« Appeler +33 6 … ? », « Confirmer ? »). */
  confirmLabel: string
  /**
   * #392 — texte VISIBLE une fois armé, quand la question entière ne tient pas
   * (ligne compacte : « Appeler ? »). Le nom accessible reste `confirmLabel`.
   */
  confirmText?: string
  /** Nom accessible au repos (porte le numéro). */
  ariaLabel: string
  disabled?: boolean
  size?: ButtonSize
  /** #392 — au repos, bouton icône (voir l'en-tête). */
  compact?: boolean
  onConfirm: () => void
}

/**
 * Le bouton armé et le bouton au repos sont deux éléments distincts en
 * variante compacte : si celui qui disparaît avait le focus (clavier), le
 * suivant le reprend. Lu AU RENDU, avant le remplacement du DOM ; sans effet
 * au doigt (Safari ne donne pas le focus à un bouton touché).
 */
function useFocusCarry(state: boolean) {
  const ref = useRef<HTMLButtonElement | null>(null)
  const carry = useRef(false)
  carry.current =
    typeof document !== 'undefined' && ref.current !== null && ref.current === document.activeElement
  useLayoutEffect(() => {
    if (carry.current) ref.current?.focus()
  }, [state])
  return ref
}

/**
 * « Rappeler » → « Appeler +33 6 12 34 56 78 ? » → compose.
 * Armé, le bouton passe au vert plein (`success` = le métier « appeler »,
 * même teinte que le bouton du composeur) : l'état se voit sans lire.
 */
export function RecallButton({
  number,
  armed,
  label,
  confirmLabel,
  confirmText,
  ariaLabel,
  disabled,
  size = 'md',
  compact = false,
  onConfirm,
}: ActionButtonProps) {
  const key = recallKey(number)
  const isArmed = armed.armedKey === key
  const ref = useFocusCarry(isArmed)
  const common = {
    ref,
    disabled,
    'data-armed-key': key,
    'data-armed': isArmed || undefined,
    // Bouton d'action dans une liste défilante : pas de double-tap-zoom.
    style: { touchAction: 'manipulation' as const },
    onClick: (event: MouseEvent<HTMLButtonElement>) =>
      armed.press(key, event.currentTarget, onConfirm),
  }
  if (compact && !isArmed) {
    return <IconButton {...common} icon={Phone} label={ariaLabel} variant="secondary" size="sm" />
  }
  return (
    <Button
      {...common}
      variant={isArmed ? 'primary' : 'secondary'}
      size={compact ? 'sm' : size}
      icon={isArmed ? PhoneCall : Phone}
      aria-label={isArmed ? confirmLabel : ariaLabel}
      className={
        isArmed ? 'bg-success-600 hover:bg-success-700 active:bg-success-700' : ''
      }
    >
      {isArmed ? (confirmText ?? confirmLabel) : label}
    </Button>
  )
}

/**
 * « Ne pas rappeler » → « Confirmer ? » → classe le numéro pour tout le
 * plateau. Libellé TOUJOURS visible au moment d'agir (jamais une icône seule
 * pour un geste destructif — DESIGN-SYSTEM §5) ; armé, il prend le contour
 * `destructive`.
 */
export function DismissButton({
  number,
  armed,
  label,
  confirmLabel,
  confirmText,
  ariaLabel,
  disabled,
  size = 'md',
  compact = false,
  onConfirm,
}: ActionButtonProps) {
  const key = dismissKey(number)
  const isArmed = armed.armedKey === key
  const ref = useFocusCarry(isArmed)
  const common = {
    ref,
    disabled,
    'data-armed-key': key,
    'data-armed': isArmed || undefined,
    style: { touchAction: 'manipulation' as const },
    onClick: (event: MouseEvent<HTMLButtonElement>) =>
      armed.press(key, event.currentTarget, onConfirm),
  }
  if (compact && !isArmed) {
    return <IconButton {...common} icon={Ban} label={ariaLabel} variant="ghost" size="sm" />
  }
  return (
    <Button
      {...common}
      variant={isArmed ? 'destructive' : 'ghost'}
      size={compact ? 'sm' : size}
      icon={Ban}
      aria-label={isArmed ? confirmLabel : ariaLabel}
    >
      {isArmed ? (confirmText ?? confirmLabel) : label}
    </Button>
  )
}
